import { ButtonBase, Typography } from '@mui/material';

import { Stars01Icon } from '@/components/icons/Stars01Icon';
import { COLORS } from '@/styles/tokens/colors';

/**
 * Side length of the square that, rotated 45°, forms the caret. Only half of
 * the rotated square's diagonal (~5.7px) protrudes past the callout's edge.
 */
const CARET_SIZE = 8;

const CALLOUT_HEIGHT = 28;

export interface ValidationNotificationCalloutProps {
  /** Whether one notification belongs to the household currently in view. */
  includesCurrentHousehold: boolean;
  /** Number of households with unread validation questions. */
  householdCount: number;
  /** Invoked when the callout is clicked. */
  onClick: () => void;
}

export function getValidationNotificationLabel(
  includesCurrentHousehold: boolean,
  householdCount: number
): string {
  if (includesCurrentHousehold && householdCount === 1) {
    return 'Validation questions for review';
  }

  if (includesCurrentHousehold) {
    const otherCount = householdCount - 1;
    const otherLabel = otherCount === 1 ? 'other' : 'others';
    return `Validation questions for review (current household & ${otherCount} ${otherLabel})`;
  }

  const householdLabel = householdCount === 1 ? 'household' : 'households';
  return `Validation questions for review (${householdCount} ${householdLabel})`;
}

/**
 * Callout that sits to the right of the app bar's inbox button and points back
 * at it, surfacing an unread validation-questions notification. Purely
 * presentational — callers decide when it renders and what clicking it does.
 */
export function ValidationNotificationCallout({
  includesCurrentHousehold,
  householdCount,
  onClick,
}: ValidationNotificationCalloutProps) {
  const label = getValidationNotificationLabel(
    includesCurrentHousehold,
    householdCount
  );

  return (
    <ButtonBase
      aria-label={label}
      onClick={onClick}
      sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        height: CALLOUT_HEIGHT,
        px: 1.25,
        borderRadius: '6px',
        border: `1px solid ${COLORS.ORANGE[100]}`,
        backgroundColor: COLORS.ORANGE[50],
        color: COLORS.ORANGE[500],
        // The caret protrudes past the left edge, so leave room for it rather
        // than letting it overlap whatever sits to the left in the app bar.
        ml: `${CARET_SIZE}px`,
        transition: 'background-color 0.2s ease, border-color 0.2s ease',
        // A square rotated 45° whose left and bottom borders become the caret's
        // two outward-facing edges. It paints above the callout's own left
        // border so no seam crosses the mouth of the caret.
        '&::before': {
          content: '""',
          position: 'absolute',
          zIndex: 1,
          top: '50%',
          left: `-${CARET_SIZE / 2}px`,
          height: CARET_SIZE,
          width: CARET_SIZE,
          transform: 'translateY(-50%) rotate(45deg)',
          backgroundColor: 'inherit',
          borderLeft: `1px solid ${COLORS.ORANGE[100]}`,
          borderBottom: `1px solid ${COLORS.ORANGE[100]}`,
        },
        '&:hover': {
          backgroundColor: COLORS.ORANGE[100],
          borderColor: COLORS.ORANGE[200],
          '&::before': {
            borderLeftColor: COLORS.ORANGE[200],
            borderBottomColor: COLORS.ORANGE[200],
          },
        },
      }}
    >
      <Stars01Icon
        size={16}
        // The stars icon is stroke-only, so it renders as an outline by
        // default; fill it so the stars read as solid, matching the design.
        fill={COLORS.ORANGE[500]}
      />
      {/* The `body-xs` variant bakes in TEXT.SECONDARY, so the callout's color
          has to be reapplied here rather than inherited from the root. */}
      <Typography variant="body-xs" noWrap sx={{ color: COLORS.ORANGE[500] }}>
        {label}
      </Typography>
    </ButtonBase>
  );
}
